import React, { useCallback, useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { PapiUser } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import GalleryMultiselect, {
  GallerySelectItem,
} from '../../common/GalleryMultiselect';
import { SubmitFormContext } from '../../context';
import { useSubmitField } from '../../hooks/useSubmitField';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useCoreMonetizationUpsellPopup } from '../../modals/CoreMonetizationUpsellPopup/useCoreMonetizationUpsellPopup';
import { initAdapter } from './adapters';
import { selectPremiumGalleries } from '../../utils/galleries';
import { deselectItems, anySelected } from '../../utils/select';

import s from './PremiumGalleryField.scss';

interface Props {}

const PremiumGalleryField: React.FC<Props> = () => {
  const { t } = useTranslation();
  const toggleId = useId();
  const { hasCoreMembership, user } = useContext(SessionContext);
  const { getValues, setValue } = useFormContext();
  const { initResponse } = useContext(SubmitFormContext) || {};
  const { username } = user as PapiUser;

  const { showCoreUpsellPopup, coreUpsellPopup } =
    useCoreMonetizationUpsellPopup();

  // Note: Submit of premium galleries happens through GalleryField
  const {
    id,
    field: { onChange, value },
  } = useSubmitField('premiumGalleries', {
    initAdapter,
  });
  const hasPremiumGalleries =
    selectPremiumGalleries(initResponse?.galleries ?? []).length > 0;
  const inPremiumGallery =
    selectPremiumGalleries(initResponse?.inGalleries ?? []).length > 0;
  const canSubmitToPremiumGallery = hasCoreMembership || inPremiumGallery;

  const handleToggleChange = useCallback(() => {
    if (!canSubmitToPremiumGallery) {
      showCoreUpsellPopup();
      return;
    }
  }, [canSubmitToPremiumGallery, showCoreUpsellPopup]);

  // When a premium gallery is selected remove all selected galleries from gallery field
  const handleSelect = useCallback(
    (newValue: GallerySelectItem[]) => {
      const values = getValues();
      if (anySelected(values['galleries'])) {
        setValue('galleries', deselectItems(values['galleries']), {
          shouldDirty: true,
        });
      }

      onChange(newValue);
    },
    [onChange, getValues, setValue]
  );

  const showCreateButton = hasCoreMembership && !hasPremiumGalleries;
  const showDropdown =
    (hasCoreMembership && hasPremiumGalleries) || inPremiumGallery;

  // toggle is only shown to trigger upsell modal when user cannot submit to PG
  const toggle = canSubmitToPremiumGallery ? null : (
    <MainToggle
      variant="main"
      id={toggleId}
      size={'small'}
      value={false}
      onChange={handleToggleChange}
      aria-labelledby={`${id}-label`}
    />
  );

  return (
    <ButtonFieldLayout
      label={
        <span className={s['label']}>
          {t('deviationSubmit.form.premiumGallery.label')}
          <SimpleTooltip
            text={t('deviationSubmit.form.premiumGallery.tooltip', {
              postProcess: ['reactPostprocessor'],
              learnMoreLink: (
                <a
                  href={t('deviationSubmit.form.premiumGallery.tooltip.url')}
                  rel="license noopener noreferrer"
                  target="_blank"
                >
                  {t('deviationSubmit.form.premiumGallery.tooltip.link')}
                </a>
              ),
            })}
            stopPropagation={true}
          >
            <InfoIcon
              className={s['tooltip-icon']}
              size={IconSize.SMALLER}
              disableDefaultColors
            />
          </SimpleTooltip>
        </span>
      }
      labelProps={{ id: `${toggleId}-label`, htmlFor: toggleId }}
      bottomSlot={
        showDropdown && (
          <GalleryMultiselect
            className={s['select']}
            id={id}
            items={value}
            placeholder={t(
              'deviationSubmit.form.premiumGallery.select.placeholder'
            )}
            label={t('deviationSubmit.form.premiumGallery.label')}
            isLabelHidden
            onChange={handleSelect}
            withSuffixIcons={false}
          />
        )
      }
    >
      {showCreateButton ? (
        <TextButton
          href={Url.userShopLink(
            username,
            USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS
          )}
          target="_blank"
          size="medium"
          variant="main"
        >
          {t('deviationSubmit.form.premiumGallery.create.label')}
        </TextButton>
      ) : (
        toggle
      )}
      {coreUpsellPopup}
    </ButtonFieldLayout>
  );
};
PremiumGalleryField.displayName = 'PremiumGalleryField';

export default PremiumGalleryField;
